import axios from 'axios';
import { API_BASE_URL, API_TIMEOUT, UPLOAD_TIMEOUT, RETRY_CONFIG, axiosConfig, uploadConfig } from '../config/api';

// =============================================================================
// AXIOS INSTANCE SETUP
// =============================================================================

// Main API client with httpOnly cookie support
const apiClient = axios.create(axiosConfig);

// Upload client with longer timeout
const uploadClient = axios.create(uploadConfig);

// Public API client for endpoints that don't require authentication (no credentials)
const publicApiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  withCredentials: false, // Don't send cookies for public endpoints
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
});

// Track if we're currently refreshing the token
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
  failedQueue.forEach(prom => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });
  failedQueue = [];
};

// =============================================================================
// REQUEST INTERCEPTORS
// =============================================================================

// Add request ID for correlation
const addRequestId = (config) => {
  config.headers['X-Request-ID'] = `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  return config;
};

apiClient.interceptors.request.use(addRequestId, (error) => Promise.reject(error));
uploadClient.interceptors.request.use(addRequestId, (error) => Promise.reject(error));
publicApiClient.interceptors.request.use(addRequestId, (error) => Promise.reject(error));

// =============================================================================
// RESPONSE INTERCEPTORS (Token Refresh + Retry Logic)
// =============================================================================

// Endpoints that should NOT trigger token refresh or redirect on 401
// These are auth-check endpoints where 401 is an expected "not authenticated" state
const skipRefreshEndpoints = ['/auth/me', '/auth/refresh', '/auth/login'];

const handleResponseError = async (error) => {
  const originalRequest = error.config;
  const requestUrl = originalRequest?.url || '';

  // Check if this is an auth-check request that should not trigger refresh/redirect
  const isAuthCheckRequest = skipRefreshEndpoints.some(ep => requestUrl.includes(ep));

  // Handle 401 - Unauthorized (token expired)
  if (error.response?.status === 401 && !originalRequest._retry) {
    // For auth-check endpoints, just reject - don't try refresh or redirect
    // This lets AuthContext handle the unauthenticated state gracefully
    if (isAuthCheckRequest) {
      return Promise.reject(error);
    }

    if (isRefreshing) {
      // If already refreshing, queue this request
      return new Promise((resolve, reject) => {
        failedQueue.push({ resolve, reject });
      }).then(() => apiClient(originalRequest));
    }

    originalRequest._retry = true;
    isRefreshing = true;

    try {
      // Attempt to refresh the token (cookies are sent automatically)
      await apiClient.post('/auth/refresh');
      processQueue(null);
      return apiClient(originalRequest);
    } catch (refreshError) {
      processQueue(refreshError, null);
      // Refresh failed - redirect to login using React-friendly navigation
      // Check if we're not already on the login page to avoid redirect loops
      if (!window.location.pathname.includes('login')) {
        window.location.href = '/company-user-login';
      }
      return Promise.reject(refreshError);
    } finally {
      isRefreshing = false;
    }
  }

  // Handle 429 - Too Many Requests (never retry these)
  if (error.response?.status === 429) {
    return Promise.reject(error);
  }

  // Handle retry for specific status codes
  if (RETRY_CONFIG.retryOn.includes(error.response?.status) && !originalRequest._retryCount) {
    originalRequest._retryCount = 0;
  }

  if (originalRequest._retryCount < RETRY_CONFIG.maxRetries && 
      RETRY_CONFIG.retryOn.includes(error.response?.status)) {
    originalRequest._retryCount++;
    const delay = RETRY_CONFIG.retryDelay * Math.pow(2, originalRequest._retryCount - 1);
    
    await new Promise(resolve => setTimeout(resolve, delay));
    return apiClient(originalRequest);
  }

  return Promise.reject(error);
};

apiClient.interceptors.response.use(
  (response) => response,
  handleResponseError
);

uploadClient.interceptors.response.use(
  (response) => response,
  handleResponseError
);

// =============================================================================
// ERROR HANDLING UTILITIES
// =============================================================================

/**
 * Extract user-friendly error message from API error
 */
export const getErrorMessage = (error) => {
  if (error.response?.data?.error?.message) {
    return error.response.data.error.message;
  }
  if (error.response?.data?.detail) {
    return error.response.data.detail;
  }
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  if (error.message) {
    return error.message;
  }
  return 'An unexpected error occurred';
};

/**
 * Extract validation errors from API response
 */
export const getValidationErrors = (error) => {
  if (error.response?.data?.error?.details) {
    return error.response.data.error.details;
  }
  return null;
};

// =============================================================================
// AUTH SERVICE
// =============================================================================

export const authService = {
  /**
   * Login with email and password
   * @returns {Promise<{user: object}>}
   */
  login: async (credentials) => {
    const response = await apiClient.post('/auth/login', {
      email: credentials.email,
      password: credentials.password,
    });
    return response.data;
  },

  /**
   * Refresh access token (uses httpOnly cookie)
   */
  refresh: async () => {
    const response = await apiClient.post('/auth/refresh');
    return response.data;
  },

  /**
   * Logout and clear cookies
   */
  logout: async () => {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Ignore errors during logout
      console.warn('Logout error:', error);
    }
  },

  /**
   * Get current authenticated user
   * @returns {Promise<{data: object}>}
   */
  getCurrentUser: async () => {
    const response = await apiClient.get('/auth/me');
    return response.data;
  },

  /**
   * Logout from all devices
   */
  logoutAll: async () => {
    const response = await apiClient.post('/auth/logout-all');
    return response.data;
  },
};

// =============================================================================
// USER SERVICE (Admin Only)
// =============================================================================

export const userService = {
  /**
   * Get all users with pagination
   */
  getUsers: async (params = {}) => {
    const response = await apiClient.get('/users', { params });
    return response.data;
  },

  /**
   * Get user by ID
   */
  getUserById: async (id) => {
    const response = await apiClient.get(`/users/${id}`);
    return response.data;
  },

  /**
   * Create new user
   */
  createUser: async (userData) => {
    const response = await apiClient.post('/users', userData);
    return response.data;
  },

  /**
   * Update user
   */
  updateUser: async (id, userData) => {
    const response = await apiClient.put(`/users/${id}`, userData);
    return response.data;
  },

  /**
   * Delete (deactivate) user
   */
  deleteUser: async (id) => {
    const response = await apiClient.delete(`/users/${id}`);
    return response.data;
  },

  /**
   * Admin reset user password
   */
  resetPassword: async (id, newPassword) => {
    const response = await apiClient.post(`/users/${id}/reset-password`, {
      new_password: newPassword,
    });
    return response.data;
  },
};

// =============================================================================
// VENDOR SERVICE
// =============================================================================

export const vendorService = {
  /**
   * Get all vendors with filtering and pagination
   */
  getVendors: async (params = {}) => {
    const response = await apiClient.get('/vendors', { params });
    return {
      data: response.data.data,
      meta: response.data.meta,
      total: response.headers['x-total-count'] || response.data.meta?.total,
    };
  },

  /**
   * Get vendor by ID with full details
   */
  getVendorById: async (id) => {
    const response = await apiClient.get(`/vendors/${id}`);
    return response.data;
  },

  /**
   * Create vendor
   */
  createVendor: async (vendorData) => {
    const response = await apiClient.post('/vendors', vendorData);
    return response.data;
  },

  /**
   * Update vendor (with optimistic locking)
   */
  updateVendor: async (id, vendorData) => {
    const response = await apiClient.put(`/vendors/${id}`, vendorData);
    return response.data;
  },

  /**
   * Soft delete vendor
   */
  deleteVendor: async (id, reason) => {
    const response = await apiClient.delete(`/vendors/${id}`, { 
      data: { reason } 
    });
    return response.data;
  },

  /**
   * Restore soft-deleted vendor
   */
  restoreVendor: async (id) => {
    const response = await apiClient.post(`/vendors/${id}/restore`);
    return response.data;
  },

  /**
   * Assign vendor code
   */
  assignVendorCode: async (id, vendorCode) => {
    const response = await apiClient.post(`/vendors/${id}/assign-code`, { vendor_code: vendorCode });
    return response.data;
  },

  /**
   * Regenerate signed MSME declaration for NON_MSME vendors
   */
  regenerateMsmeDeclaration: async (id) => {
    const response = await apiClient.post(`/vendors/${id}/msme-declaration/regenerate`);
    return response.data;
  },

  /**
   * Change vendor status
   */
  changeStatus: async (id, status, reason) => {
    // Convert status to uppercase to match backend validation
    const normalizedStatus = typeof status === 'string' ? status.toUpperCase() : status;
    const response = await apiClient.patch(`/vendors/${id}/status`, { status: normalizedStatus, reason });
    return response.data;
  },

  /**
   * Get vendor audit log
   */
  getAuditLog: async (id, limit = 50) => {
    const response = await apiClient.get(`/vendors/${id}/audit-log`, { params: { limit } });
    return response.data;
  },

  /**
   * Get vendor statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/vendors/stats');
    return response.data;
  },

  /**
   * Bulk status update
   */
  bulkStatusUpdate: async (vendorIds, status, reason) => {
    const response = await apiClient.post('/vendors/bulk/status', {
      vendor_ids: vendorIds,
      status,
      reason,
    });
    return response.data;
  },

  /**
   * Create rectification link for rejected vendor
   */
  createRectificationLink: async (vendorId, data) => {
    const response = await apiClient.post(`/vendors/${vendorId}/create-rectification-link`, data);
    return response.data;
  },

  /**
   * Permanently delete a rejected vendor
   */
  permanentDeleteVendor: async (vendorId) => {
    const response = await apiClient.delete(`/vendors/${vendorId}/permanent-delete`);
    return response.data;
  },

  /**
   * Bulk delete
   */
  bulkDelete: async (vendorIds, reason) => {
    const response = await apiClient.post('/vendors/bulk/delete', {
      vendor_ids: vendorIds,
      reason,
    });
    return response.data;
  },
};

// =============================================================================
// DOCUMENT SERVICE
// =============================================================================

export const documentService = {
  /**
   * Get documents for a vendor
   */
  getVendorDocuments: async (vendorId) => {
    const response = await apiClient.get(`/documents/vendor/${vendorId}`);
    return response.data;
  },

  /**
   * Get document by ID
   */
  getDocumentById: async (id) => {
    const response = await apiClient.get(`/documents/${id}`);
    return response.data;
  },

  /**
   * Download document
   */
  downloadDocument: async (id) => {
    const response = await apiClient.get(`/documents/${id}/download`, {
      responseType: 'blob',
    });
    return response;
  },

  /**
   * Upload document for vendor
   */
  uploadDocument: async (vendorId, file, documentType, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('document_type', documentType);

    const response = await uploadClient.post(`/documents/vendor/${vendorId}/upload`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined,
    });
    return response.data;
  },

  /**
   * Update document status
   */
  updateStatus: async (id, status) => {
    const response = await apiClient.patch(`/documents/${id}/status`, { status });
    return response.data;
  },

  /**
   * Delete document
   */
  deleteDocument: async (id) => {
    const response = await apiClient.delete(`/documents/${id}`);
    return response.data;
  },

  /**
   * Reject multiple documents for a vendor
   */
  rejectMultipleDocuments: async (vendorId, documentIds, rejectionReason) => {
    const response = await apiClient.post(`/documents/vendor/${vendorId}/reject-multiple`, {
      document_ids: documentIds,
      rejection_reason: rejectionReason,
    });
    return response.data;
  },

  getReplacementInfo: async (token) => {
    const response = await apiClient.get(`/documents/replacement/${token}`);
    return response.data;
  },

  submitReplacements: async (token, replacements, files = []) => {
    const formData = new FormData();
    formData.append('replacements', JSON.stringify(replacements));
    replacements.forEach((r, index) => {
      const file = Array.isArray(files) ? files[index] : files[r.document_id];
      if (file) {
        formData.append(`file_${r.document_id}`, file);
      }
    });
    const response = await uploadClient.post(
      `/documents/replacement/${token}/submit`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    return response.data;
  },

  /**
   * Get document statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/documents/stats');
    return response.data;
  },
};

// =============================================================================
// REGISTRATION SERVICE (Staged 7-day Flow)
// =============================================================================

export const registrationService = {
  /**
   * Start a new registration session (public endpoint)
   */
  startSession: async (inviteToken, clientFingerprint) => {
    const response = await publicApiClient.post('/registration/start', {
      invite_token: inviteToken,
      client_fingerprint: clientFingerprint,
    });
    return response.data;
  },

  /**
   * Resume existing session (public endpoint)
   */
  resumeSession: async (sessionId) => {
    const response = await publicApiClient.get(`/registration/${sessionId}`);
    return response.data;
  },

  /**
   * Save step data (public endpoint)
   */
  saveStepData: async (sessionId, step, data) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/data`, {
      step,
      data,
    });
    return response.data;
  },

  /**
   * Upload file for registration (public endpoint)
   */
  uploadFile: async (sessionId, file, fileType, onProgress) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('file_type', fileType);

    // Create a public upload client without credentials
    const publicUploadClient = axios.create({
      baseURL: API_BASE_URL,
      timeout: UPLOAD_TIMEOUT,
      withCredentials: false,
      headers: {
        'Accept': 'application/json',
      },
    });

    const response = await publicUploadClient.post(`/registration/${sessionId}/files`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: onProgress ? (e) => onProgress(Math.round((e.loaded * 100) / e.total)) : undefined,
    });
    return response.data;
  },

  /**
   * Delete staged file (public endpoint)
   */
  deleteFile: async (sessionId, fileId) => {
    const response = await publicApiClient.delete(`/registration/${sessionId}/files/${fileId}`);
    return response.data;
  },

  /**
   * Preview a staged file (public endpoint)
   */
  previewStagedFile: async (sessionId, fileId) => {
    const response = await publicApiClient.get(
      `/registration/${sessionId}/files/${fileId}/preview`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Submit registration (public endpoint)
   */
  submit: async (sessionId, submissionId, generatedAgreements = []) => {
    const response = await publicApiClient.post(`/registration/${sessionId}/submit`, {
      submission_id: submissionId,
      generated_agreements: generatedAgreements,
    });
    return response.data;
  },
};

// =============================================================================
// ACKNOWLEDGEMENT SERVICE
// =============================================================================

export const acknowledgementService = {
  /**
   * Validate acknowledgement token (public endpoint)
   */
  validateToken: async (token) => {
    const response = await publicApiClient.get(`/acknowledgements/validate/${token}`);
    return response.data;
  },

  /**
   * Vendor approves the questionnaire (public endpoint)
   */
  approve: async (token) => {
    const response = await publicApiClient.post(`/acknowledgements/${token}/approve`);
    return response.data;
  },

  /**
   * Vendor conflicts with questionnaire and proposes changes (public endpoint)
   */
  conflict: async (token, data) => {
    const response = await publicApiClient.post(`/acknowledgements/${token}/conflict`, data);
    return response.data;
  },

  /**
   * Get conflicted acknowledgements for admin review
   */
  getConflicted: async (page = 1, limit = 25, search = '') => {
    const response = await apiClient.get('/acknowledgements/conflicted', {
      params: { page, limit, search },
    });
    return response.data;
  },

  /**
   * Admin resolves conflicted acknowledgement
   * @param {number} id - Acknowledgement ID
   * @param {Object} data - Optional data with final_values for admin's selected values
   */
  resolve: async (id, data = {}) => {
    const response = await apiClient.post(`/acknowledgements/${id}/resolve`, data);
    return response.data;
  },

  /**
   * Admin rejects vendor for a conflicted acknowledgement
   * @param {number} id - Acknowledgement ID
   */
  reject: async (id) => {
    const response = await apiClient.post(`/acknowledgements/${id}/reject`);
    return response.data;
  },

  /**
   * Resend acknowledgement email
   */
  resend: async (id) => {
    const response = await apiClient.post(`/acknowledgements/${id}/resend`);
    return response.data;
  },
};

// =============================================================================
// INVITE SERVICE
// =============================================================================

export const inviteService = {
  /**
   * Get all invites with filtering
   */
  getInvites: async (params = {}) => {
    const response = await apiClient.get('/invites', { params });
    return response.data;
  },

  /**
   * Get invite by ID
   */
  getInviteById: async (id) => {
    const response = await apiClient.get(`/invites/${id}`);
    return response.data;
  },

  /**
   * Validate invite token (public endpoint)
   */
  validateToken: async (token) => {
    const response = await publicApiClient.get(`/invites/validate/${token}`);
    return response.data;
  },

  /**
   * Create new invite
   */
  createInvite: async (data) => {
    const response = await apiClient.post('/invites', data);
    return response.data;
  },

  /**
   * Delete invite
   */
  deleteInvite: async (id) => {
    const response = await apiClient.delete(`/invites/${id}`);
    return response.data;
  },

  /**
   * Resend invite
   */
  resendInvite: async (id) => {
    const response = await apiClient.post(`/invites/${id}/resend`);
    return response.data;
  },
};

// =============================================================================
// LINK TOKEN SERVICE
// =============================================================================
// Cross-stage vendor link registry (/api/v1/link-tokens). Surfaces registration,
// rectification, document-replacement, acknowledgement and regularization links
// in one place, with extend/revoke/regenerate actions and per-vendor journey.

export const linkTokenService = {
  /**
   * List link tokens with filtering + server-side search + pagination.
   * Array params (stage, status) are passed through as arrays; axios serializes
   * them as repeated query keys which the backend parses as multi-select.
   */
  getLinkTokens: async (params = {}) => {
    const response = await apiClient.get('/link-tokens', { params });
    return response.data;
  },

  /**
   * Per-stage counts (+ total) for the given filters (stage ignored). Powers the
   * stage tab badges. Returns { counts: { REGISTRATION: n, ... }, total }.
   */
  getStageCounts: async (params = {}) => {
    const response = await apiClient.get('/link-tokens/stage-counts', { params });
    return response.data;
  },

  /**
   * Full onboarding chain for one vendor (supersede chains resolved).
   */
  getJourney: async (vendorId) => {
    const response = await apiClient.get(`/link-tokens/journey/${vendorId}`);
    return response.data;
  },

  /**
   * Append-only event trail for one token.
   */
  getEvents: async (id) => {
    const response = await apiClient.get(`/link-tokens/${id}/events`);
    return response.data;
  },

  /**
   * Current live URL for a token (server reads the raw token from the source
   * table). Returns { live, link }.
   */
  getLink: async (id) => {
    const response = await apiClient.get(`/link-tokens/${id}/link`);
    return response.data;
  },

  /**
   * Extend expiry. body: { days, reason }
   */
  extend: async (id, body) => {
    const response = await apiClient.post(`/link-tokens/${id}/extend`, body);
    return response.data;
  },

  /**
   * Revoke. body: { reason }
   */
  revoke: async (id, body) => {
    const response = await apiClient.post(`/link-tokens/${id}/revoke`, body);
    return response.data;
  },

  /**
   * Regenerate. body: { expires_in_days? }
   */
  regenerate: async (id, body = {}) => {
    const response = await apiClient.post(`/link-tokens/${id}/regenerate`, body);
    return response.data;
  },

  /**
   * Nudge the vendor about a still-open link. Shares the hourly worker's 24h
   * per-recipient rate limit, so this can legitimately come back as a 422.
   */
  remind: async (id) => {
    const response = await apiClient.post(`/link-tokens/${id}/remind`);
    return response.data;
  },
};

// =============================================================================
// MIS REPORT SERVICE (read-only management report; terminal-state population)
// =============================================================================
export const misReportService = {
  getSummary: async (params) => (await apiClient.get('/reports/mis/summary', { params })).data.data,
  getTrend: async (params) => (await apiClient.get('/reports/mis/trend', { params })).data.data,
  getDistribution: async (dimension, params) =>
    (await apiClient.get('/reports/mis/distribution', { params: { dimension, ...params } })).data.data,
  getPipeline: async (params) => (await apiClient.get('/reports/mis/pipeline', { params })).data.data,
  getInFlight: async () => (await apiClient.get('/reports/mis/pipeline/in-flight')).data.data,
  getRecords: async (params) => (await apiClient.get('/reports/mis/records', { params })).data.data,
  exportExcel: async (params) => {
    const response = await apiClient.get('/reports/mis/export', { params, responseType: 'blob' });
    return response.data;
  },
};

// =============================================================================
// APPROVAL SERVICE
// =============================================================================

export const approvalService = {
  /**
   * Get all approvals with filtering
   */
  getApprovals: async (params = {}) => {
    const response = await apiClient.get('/approvals', { params });
    return response.data;
  },

  /**
   * Get pending approvals
   * @param {string} level - Approval level (LEVEL_1, LEVEL_2, FINAL)
   * @param {string} stage - Workflow stage (document_verification, level1)
   */
  getPendingApprovals: async (level, stage) => {
    const params = {};
    if (level) params.level = level;
    if (stage) params.stage = stage;
    const response = await apiClient.get('/approvals/pending', { params });
    return response.data;
  },

  fillQuestionnaire: async (vendorId, data) => {
    const response = await apiClient.post(`/approvals/vendor/${vendorId}/fill-questionnaire`, data);
    return response.data;
  },
  assignVendorCode: async (vendorId, data) => {
    const response = await apiClient.post(`/approvals/vendor/${vendorId}/assign-vendor-code`, data);
    return response.data;
  },
  /**
   * Get approval statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/approvals/stats');
    return response.data;
  },

  /**
   * Get approval history for vendor
   */
  getVendorHistory: async (vendorId) => {
    const response = await apiClient.get(`/approvals/vendor/${vendorId}/history`);
    return response.data;
  },

  /**
   * Submit approval decision
   */
  submitDecision: async (vendorId, level, status, comments, questionnaire) => {
    const response = await apiClient.post('/approvals', {
      vendor_id: vendorId,
      level,
      status,
      comments,
      questionnaire,
    });
    return response.data;
  },
};

// =============================================================================
// REGULARIZATION SERVICE
// =============================================================================

export const regularizationService = {
  /**
   * Get regularization statistics
   */
  getStats: async () => {
    const response = await apiClient.get('/regularization/stats');
    return response.data;
  },

  /**
   * List batches with pagination and filtering
   */
  getBatches: async (page = 1, limit = 25, status, search) => {
    const params = { page, limit };
    if (status) params.status = status;
    if (search) params.search = search;
    const response = await apiClient.get('/regularization/batches', { params });
    return response.data;
  },

  /**
   * Get batch details
   */
  getBatchDetails: async (batchId) => {
    const response = await apiClient.get(`/regularization/batch/${batchId}`);
    return response.data;
  },

  /**
   * Get eligible vendors for regularization
   */
  getEligibleVendors: async (search) => {
    const params = search ? { search } : {};
    const response = await apiClient.get('/regularization/eligible-vendors', { params });
    return response.data;
  },

  /**
   * Send bulk regularization links
   */
  sendBulk: async (vendorIds) => {
    const response = await apiClient.post('/regularization/send-bulk', {
      vendor_ids: vendorIds.map(id => Number(id)), // Ensure numbers are sent
    });
    return response.data;
  },

  /**
   * Get request by token (public)
   */
  getRequestByToken: async (token) => {
    const response = await apiClient.get(`/regularization/request/${token}`);
    return response.data;
  },

  /**
   * Preview existing vendor document by token + type (public, for "Keep existing" in regularization flow)
   */
  previewDocument: async (token, documentType) => {
    const response = await apiClient.get(
      `/regularization/request/${token}/documents/${documentType}/preview`,
      { responseType: 'blob' }
    );
    return response.data;
  },

  /**
   * Submit vendor updates (public)
   */
  submitUpdates: async (token, data) => {
    const response = await apiClient.post(`/regularization/submit/${token}`, data);
    return response.data;
  },

  /**
   * Verify request (admin)
   */
  verifyRequest: async (requestId) => {
    const response = await apiClient.post(`/regularization/verify/${requestId}`);
    return response.data;
  },
};

// =============================================================================
// SUPPLIER REQUEST SERVICE
// =============================================================================

export const supplierRequestService = {
  /**
   * Get all supplier requests
   */
  getSupplierRequests: async (params = {}) => {
    const response = await apiClient.get('/supplier-requests', { params });
    return response.data;
  },

  /**
   * Downstream onboarding status per request (invite -> vendor -> docs ->
   * approvals -> code). USER gets own requests; admin/coordinator all.
   */
  getJourneyStatus: async () => {
    const response = await apiClient.get('/supplier-requests/journey-status');
    return response.data;
  },

  /**
   * Get request by ID
   */
  getRequestById: async (id) => {
    const response = await apiClient.get(`/supplier-requests/${id}`);
    return response.data;
  },

  /**
   * Create draft request
   */
  createDraft: async (data) => {
    const response = await apiClient.post('/supplier-requests', { ...data, status: 'DRAFT' });
    return response.data;
  },

  /**
   * Update draft request
   */
  updateDraft: async (id, data) => {
    const response = await apiClient.put(`/supplier-requests/${id}`, data);
    return response.data;
  },

  /**
   * Submit request
   */
  submitRequest: async (id) => {
    const response = await apiClient.post(`/supplier-requests/${id}/submit`);
    return response.data;
  },

  /**
   * Approve request
   */
  approveRequest: async (id) => {
    const response = await apiClient.post(`/supplier-requests/${id}/approve`);
    return response.data;
  },

  /**
   * Reject request
   */
  rejectRequest: async (id, reason) => {
    const response = await apiClient.post(`/supplier-requests/${id}/reject`, { reason });
    return response.data;
  },

  /**
   * Delete request
   */
  deleteRequest: async (id) => {
    const response = await apiClient.delete(`/supplier-requests/${id}`);
    return response.data;
  },
};

// =============================================================================
// DASHBOARD SERVICE
// =============================================================================

export const dashboardService = {
  /**
   * Get dashboard metrics
   */
  getMetrics: async () => {
    const response = await apiClient.get('/dashboard/metrics');
    const data = response.data.data;
    
    // Transform backend response to frontend expected format
    return {
      data: {
        totalVendors: {
          value: data.vendors?.total || 0,
          change: '+12%',
          changeType: 'positive',
        },
        pendingApprovals: {
          value: (data.approvals?.pending_by_level?.LEVEL_1 ?? 0) + (data.approvals?.level1_awaiting_ack ?? 0),
          change: `+${data.approvals?.pending_by_level?.LEVEL_1 || 0}`,
          changeType: 'neutral',
        },
        rejectedVendors: {
          value: data.vendors?.by_status?.REJECTED || 0,
          change: '',
          changeType: 'neutral',
        },
        thisMonthOnboarded: {
          value: data.vendors?.this_month_onboarded || 0,
          change: '+18%',
          changeType: 'positive',
        },
        vendorsNeedingDocVerification: {
          value: data.vendors?.needing_doc_verification || 0,
          change: '',
          changeType: 'neutral',
        },
        vendorDistribution: data.vendors?.by_status || {},
        countrySplit: data.vendors?.by_country || [],
        msmeSplit: data.vendors?.by_msme || {},
        recentVendors: data.recent_vendors || [],
      },
    };
  },

  /**
   * Get onboarding trends
   */
  getOnboardingTrends: async () => {
    const response = await apiClient.get('/dashboard/onboarding-trends');
    return response.data;
  },

  /**
   * Get activity feed
   */
  getActivityFeed: async (limit = 20) => {
    const response = await apiClient.get('/dashboard/activity-feed', { params: { limit } });
    return response.data;
  },

  /**
   * Get workflow statistics
   */
  getWorkflowStats: async () => {
    const response = await apiClient.get('/dashboard/workflow-stats');
    return response.data;
  },
};

// =============================================================================
// HEALTH SERVICE
// =============================================================================

export const healthService = {
  /**
   * Check API health
   */
  check: async () => {
    const response = await apiClient.get('/health', { 
      baseURL: API_BASE_URL.replace('/api/v1', ''),
      timeout: 5000,
    });
    return response.data;
  },
};

// Export default client for custom requests
export default apiClient;
